import * as React from "react"
import {useEffect, useRef, useState} from "react"
import {useIntersection} from "react-use";
import {Box, CircularProgress} from "@mui/material";


const DEFAULT_IMAGE = "/images/defaultImage.png"

// The image itself, with its own loaded state. ImageSeeker's `useSpinner` keeps that state in a
// Preact signal and hides the <img> until `onLoad` flips it; on the big candidate lists React 18
// renders the cards concurrently and the signal's re-render is lost now and then — the handler
// runs, the image is complete in the browser, and the card stays on its spinner for good
// (869fkmx4a, reproduced on the production bundle with CPU throttling: 2–10 stuck cards per
// product change). Plain React state from the mounted handler is not subject to that, and the
// effect below reads the element on mount for the image that was already in cache and finished
// before any handler could see it.
function LoadedImage({ src, alt = "", sx = {}, useSpinner = false, spinnerSx = {}, ...props }) {
	const imgRef = useRef(null)
	const [loaded, setLoaded] = useState(false)

	useEffect(() => {
		const img = imgRef.current
		if (!img) return
		if (!img.complete) {
			setLoaded(false)
			return
		}
		if (img.naturalWidth === 0 && !img.src.endsWith(DEFAULT_IMAGE))
			img.src = DEFAULT_IMAGE
		setLoaded(true)
	}, [src])

	const showSpinner = useSpinner && !loaded

	return <>
		{ showSpinner && <Box display="flex" justifyContent="center" alignItems="center" sx={{ backgroundColor: 'background.neutral', ...spinnerSx }}>
			<CircularProgress size={24} />
		</Box> }
		<Box component="span" sx={{ lineHeight: 1, overflow: 'hidden', position: 'relative', display: showSpinner ? 'none' : 'block' }}>
			<Box
				component="img"
				ref={imgRef}
				src={src}
				alt={alt}
				{...props}
				sx={{ objectFit: 'cover', ...sx }}
				onLoad={() => setLoaded(true)}
				onError={event => {
					event.target.src = DEFAULT_IMAGE
					setLoaded(true)
				}}
			/>
		</Box>
	</>
}

// Visibility gate: a placeholder of the same size holds the spot until the card scrolls near the
// viewport, then the image mounts and stays mounted. Ancestors with their own scroll clip the
// intersection, so a list inside an overflow:auto box only fetches what is in view. ImageSeeker's
// own `lazyLoad` cannot do this with a spinner: the spinner keeps the <img> box at display:none
// until onLoad, and a hidden box never intersects (1.41.5 shipped exactly that deadlock).
export default function MatchersLazyImage({ sx = {}, rootMargin = '300px', ...props }) {
	const ref = useRef(null)
	const seen = useRef(false)
	const entry = useIntersection(ref, { rootMargin })
	if (entry?.isIntersecting)
		seen.current = true

	const { width, height, maxHeight } = sx
	const placeholderHeight = (height && height !== 'auto') ? height : (maxHeight || width)
	const placeholderSx = { width, height: placeholderHeight, flexShrink: 0 }

	return <Box ref={ref} display="inline-flex">
		{ seen.current
			? <LoadedImage sx={sx} spinnerSx={placeholderSx} {...props} />
			: <Box sx={placeholderSx} /> }
	</Box>
}
